import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { useGlobalAnalytics, useWidgetPreferences, useSaveWidgetPreferences } from '@/queries/analytics';
import { DashboardFilters } from '@/components/analytics/DashboardFilters';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { WidgetCustomizer } from '@/components/analytics/WidgetCustomizer';
import { WidgetSkeleton } from '@/components/analytics/Skeleton';
import { ErrorBoundary } from '@/components/analytics/ErrorBoundary';
import type { AnalyticsFilters, TimeRange, WidgetId } from '@/types/analytics';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import styles from './index.module.scss';

export const Route = createFileRoute('/analytics/')({
  component: GlobalAnalyticsDashboard,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
  validateSearch: (search: Record<string, unknown>): { timeRange?: TimeRange; isDailyKnowledge?: boolean } => {
    return {
      timeRange: (search.timeRange as TimeRange) || undefined,
      isDailyKnowledge: search.isDailyKnowledge === true || search.isDailyKnowledge === 'true',
    };
  },
});

function GlobalAnalyticsDashboard() {
  const searchParams = Route.useSearch();

  const [filters, setFilters] = useState<AnalyticsFilters>({
    timeRange: (searchParams.timeRange as TimeRange) || 'last30',
    isDailyKnowledge: searchParams.isDailyKnowledge || false,
  });

  const { isAdmin } = useAdminStatus();
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Fetch analytics data and preferences
  const { data, isLoading, error } = useGlobalAnalytics(filters);
  const { data: preferences } = useWidgetPreferences();
  const savePreferences = useSaveWidgetPreferences();

  // Get global dashboard preferences
  const globalPrefs = preferences?.find((p) => p.dashboardType === 'global');
  const enabledWidgets = globalPrefs?.enabledWidgets || [
    'overall-success-rate',
    'calendar-heatmap',
    'daily-knowledge-streak',
    'improvement-trends',
    'total-study-time',
    'session-completion-rate',
    'performance-by-type',
    'performance-by-difficulty',
    'participation-trends',
  ];
  const widgetOrder = globalPrefs?.widgetOrder || enabledWidgets;

  const handleFiltersChange = (newFilters: Partial<AnalyticsFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleWidgetOrderChange = (newOrder: WidgetId[]) => {
    savePreferences.mutate({
      dashboardType: 'global',
      enabledWidgets,
      widgetOrder: newOrder,
      widgetSettings: globalPrefs?.widgetSettings || ({} as Record<WidgetId, any>),
    });
  };

  const handleSaveWidgets = (selectedWidgets: WidgetId[]) => {
    savePreferences.mutate({
      dashboardType: 'global',
      enabledWidgets: selectedWidgets,
      widgetOrder: selectedWidgets,
      widgetSettings: globalPrefs?.widgetSettings || ({} as Record<WidgetId, any>),
    });
  };

  if (isLoading) {
    return (
      <div className={styles.analytics}>
        <div className={styles.analytics__header}>
          <div>
            <h1 className={styles.analytics__title}>
              {filters.isDailyKnowledge ? 'Daily Knowledge Analytics' : 'Analytics Dashboard'}
            </h1>
            <p className={styles.analytics__subtitle}>
              {filters.isDailyKnowledge
                ? 'Track your daily review progress and streaks'
                : 'Track your learning progress and performance across all quizlets'}
            </p>
          </div>
        </div>

        <div className={styles.analytics__filters}>
          <div className={styles.analytics__filtersPlaceholder} />
        </div>

        <div className={styles.analytics__content}>
          <div className={styles.analytics__skeletonGrid}>
            {Array.from({ length: 6 }).map((_, index) => (
              <WidgetSkeleton key={index} linesCount={3} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.analytics__errorContainer}>
        <div className={styles.analytics__errorContent}>
          <h2 className={styles.analytics__errorTitle}>Error Loading Analytics</h2>
          <p className={styles.analytics__errorMessage}>{error.message}</p>
          <button
            className={styles.analytics__retryButton}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={styles.analytics}>
        <div className={styles.analytics__header}>
          <div>
            <h1 className={styles.analytics__title}>
              {filters.isDailyKnowledge ? 'Daily Knowledge Analytics' : 'Analytics Dashboard'}
            </h1>
            <p className={styles.analytics__subtitle}>
              {filters.isDailyKnowledge
                ? 'Track your daily review progress and streaks'
                : 'Track your learning progress and performance across all quizlets'}
            </p>
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <motion.button
              className={styles.analytics__customizeButton}
              onClick={() => setIsEditMode(!isEditMode)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: isEditMode ? 'var(--primary-color, #0f766e)' : 'transparent',
                color: isEditMode ? 'white' : 'var(--text-primary)',
                border: isEditMode ? '1px solid var(--primary-color, #0f766e)' : '1px solid var(--border-color, #e5e5e5)',
              }}
            >
              {isEditMode ? 'Done Organizing' : 'Rearrange Layout'}
            </motion.button>
            <motion.button
              className={styles.analytics__customizeButton}
              onClick={() => setIsCustomizerOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Customize Widgets
            </motion.button>
          </div>
        )}


        <DashboardFilters
          filters={filters}
          onChange={handleFiltersChange}
          showDailyKnowledgeToggle
        />


        <div className={styles.analytics__content}>
          <AnalyticsDashboard
            data={data ?? null}
            isLoading={isLoading}
            error={error}
            enabledWidgets={enabledWidgets}
            widgetOrder={widgetOrder}
            onWidgetOrderChange={handleWidgetOrderChange}
            isEditMode={isEditMode}
          />
        </div>

        <WidgetCustomizer
          isOpen={isCustomizerOpen}
          onClose={() => setIsCustomizerOpen(false)}
          enabledWidgets={enabledWidgets}
          onSave={handleSaveWidgets}
        />
      </div>
    </ErrorBoundary >
  );
}
